import React, { useState, useEffect } from 'react';
import { Search, MessageCircle, Settings, X, PenSquare, LogOut, Shield, Bell, Languages, User, Edit2, Check, Globe, ChevronDown } from 'lucide-react';
import { Input } from './ui/input';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSiteTitle } from '../contexts/SiteTitleContext';
import NotificationPanel from './NotificationPanel';
import YegoLogo from './YegoLogo';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Language configuration
const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'rw', label: 'Kinyarwanda', short: 'RW' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'sw', label: 'Kiswahili', short: 'SW' }
];

// Component to show pending articles count badge
const PendingBadge = () => {
  const [count, setCount] = useState(0);
  const { isAdmin } = useAuth();

  useEffect(() => {
    if (!isAdmin) return;
    
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('yegoNewsToken');
        const response = await axios.get(`${BACKEND_URL}/api/editorial/pending-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCount(response.data.count);
      } catch (error) {
        console.error('Failed to fetch pending count:', error);
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  if (count === 0) return null;
  
  return (
    <span className="ml-auto px-1.5 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
      {count}
    </span>
  );
};

// Category configuration with translation keys
const getCategoryConfig = (t) => [
  { id: 'home', label: t('menu.home'), translationKey: 'menu.home' },
  { id: 'videos', label: t('menu.videos') || 'Videos', translationKey: 'menu.videos' },
  { id: 'library', label: 'Library', translationKey: 'menu.library' },
  { id: 'local', label: t('menu.local'), translationKey: 'menu.local' },
];

const RwandaNewsHeader = ({ 
  activeCategory, 
  setActiveCategory, 
  onOpenCreator,
  onOpenAuth,
  onOpenAdmin,
  onOpenSettings,
  onOpenHelp,
  onOpenSendMessage,
  darkMode,
  setDarkMode 
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [categoryOrder, setCategoryOrder] = useState(() => {
    const savedOrder = localStorage.getItem('yegoNews_categoryOrder');
    if (savedOrder) {
      try {
        return JSON.parse(savedOrder);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [editingTitle, setEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState({ part1: '', part2: '' });
  
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { language, setLanguage, siteDefaultLanguage, setSiteDefaultLanguage, hasUserPreference, resetToSiteDefault, t } = useLanguage();
  const { siteTitle, updateSiteTitle } = useSiteTitle();
  
  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];
  
  // Get categories with translations
  const defaultCategories = getCategoryConfig(t);
  
  // Apply saved order to categories
  const categories = categoryOrder 
    ? categoryOrder.map(id => defaultCategories.find(c => c.id === id)).filter(Boolean)
    : defaultCategories;

  // Handle drag start
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newCategories = [...categories];
    const draggedItem = newCategories[draggedIndex];
    newCategories.splice(draggedIndex, 1);
    newCategories.splice(index, 0, draggedItem);
    
    const newOrder = newCategories.map(c => c.id);
    localStorage.setItem('yegoNews_categoryOrder', JSON.stringify(newOrder));
    setCategoryOrder(newOrder);
    setDraggedIndex(index);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Fetch unread notification count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!isAuthenticated) return;
      try {
        const token = localStorage.getItem('yegoNewsToken');
        const response = await axios.get(`${BACKEND_URL}/api/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadCount(response.data.count);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Close menus when clicking outside
  const closeAllMenus = () => {
    setShowUserMenu(false);
    setShowLanguageMenu(false);
  };

  return (
    <header className="bg-white/95 dark:bg-gray-950/95 backdrop-blur-md border-b border-gray-200/80 dark:border-gray-800/80 sticky top-0 z-40 shadow-[0_4px_20px_-4px_rgba(16,185,129,0.06)]">
      <div className="max-w-7xl mx-auto">
        {/* Top Row */}
        <div className="flex items-center justify-between px-4 py-3 gap-4">
          {/* Logo & Brand with Backlight */}
          <div className="flex items-center gap-2 group relative flex-shrink-0">
            <a href="/" className="flex items-center gap-2.5 relative select-none">
              {/* Brand Backlight Ambient Aura */}
              <div 
                className="absolute -inset-x-3 -inset-y-1.5 rounded-2xl bg-gradient-to-r from-emerald-500/25 via-teal-500/18 to-amber-500/15 blur-lg opacity-60 group-hover:opacity-100 group-hover:blur-xl transition-all duration-300 pointer-events-none"
              />

              <YegoLogo size={42} withBacklight={true} />

              <span 
                className="text-2xl font-black tracking-tight leading-none bg-gradient-to-r from-emerald-800 via-emerald-600 to-teal-600 dark:from-white dark:via-emerald-200 dark:to-teal-300 bg-clip-text text-transparent drop-shadow-[0_1px_6px_rgba(16,185,129,0.25)] transition-all group-hover:drop-shadow-[0_2px_12px_rgba(16,185,129,0.45)] relative z-10"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                {siteTitle?.part1 || 'YEGO'}
              </span>
            </a>
            {isAdmin && !editingTitle && (
              <button
                onClick={() => {
                  setTempTitle({ part1: siteTitle.part1, part2: siteTitle.part2 });
                  setEditingTitle(true);
                }}
                className="ml-1 p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-opacity"
                title="Edit site title"
              >
                <Edit2 className="h-3.5 w-3.5 text-gray-400" />
              </button>
            )}
            {editingTitle && (
              <div className="flex items-center gap-1 ml-2">
                <input
                  type="text"
                  value={tempTitle.part1}
                  onChange={(e) => setTempTitle({ ...tempTitle, part1: e.target.value })}
                  className="w-16 text-lg font-bold text-[#047857] bg-transparent border-b-2 border-[#047857] outline-none"
                  placeholder="YEGO"
                  autoFocus
                />
                <button
                  onClick={async () => {
                    if (tempTitle.part1.trim()) {
                      await updateSiteTitle(tempTitle);
                      setEditingTitle(false);
                    }
                  }}
                  className="p-1 bg-emerald-500 text-white rounded-full hover:bg-emerald-600"
                >
                  <Check className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setEditingTitle(false)}
                  className="p-1 bg-gray-200 dark:bg-gray-700 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  <X className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                </button>
              </div>
            )}
          </div>

          {/* Search - Desktop only, hidden on mobile */}
          <div className="hidden md:flex flex-1 max-w-2xl">
            <div className="relative w-full">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search for topics, locations & sources"
                className="pl-12 pr-4 py-2.5 w-full bg-gray-100 dark:bg-gray-800 border-0 rounded-full focus:ring-2 focus:ring-[#D90429] text-sm"
              />
            </div>
          </div>

          {/* Single User Menu Button */}
          <div className="relative flex-shrink-0">
            <button 
              onClick={() => setShowUserMenu(!showUserMenu)}
              data-testid="user-menu-trigger"
              className="flex items-center gap-2 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
            >
              {isAuthenticated ? (
                <div className="w-9 h-9 rounded-full bg-[#D90429] text-white font-medium flex items-center justify-center text-sm overflow-hidden">
                  {user?.avatar ? (
                    <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    user?.name?.charAt(0).toUpperCase() || 'U'
                  )}
                </div>
              ) : (
                <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                  <User className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </div>
              )}
              <ChevronDown className="h-4 w-4 text-gray-400 hidden sm:block" />
            </button>
            
            {/* Unified Dropdown Menu */}
            {showUserMenu && (
              <>
                <div 
                  className="fixed inset-0 z-40 bg-transparent" 
                  onClick={closeAllMenus} 
                  aria-hidden="true"
                />
                <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-800 z-50 overflow-hidden">
                  
                  {/* Sign In / Profile Section */}
                  {isAuthenticated ? (
                    <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-r from-emerald-50 to-white dark:from-emerald-900/20 dark:to-gray-900">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center text-lg overflow-hidden">
                          {user?.avatar ? (
                            <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                          ) : (
                            user?.name?.charAt(0).toUpperCase() || 'U'
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 dark:text-white truncate">{user?.name}</p>
                          <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                          <p className="text-xs text-emerald-600 font-medium capitalize mt-0.5">{user?.role || (user?.is_admin ? 'Admin' : 'Contributor')}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 border-b border-gray-200 dark:border-gray-800">
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          onOpenAuth?.();
                        }}
                        data-testid="sign-in-btn"
                        className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <User className="h-4 w-4" />
                        Sign In
                      </button>
                      <p className="text-xs text-gray-500 text-center mt-2">Sign in to save articles and more</p>
                    </div>
                  )}

                  {/* Menu Items */}
                  <div className="p-2">
                    {/* Notifications */}
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        setShowNotifications(true);
                      }}
                      data-testid="menu-notifications"
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg relative"
                    >
                      <Bell className="h-5 w-5 text-gray-500" />
                      <span>Notifications</span>
                      {unreadCount > 0 && (
                        <span className="ml-auto px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </button>

                    {/* Language */}
                    <div className="relative">
                      <button
                        onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                        data-testid="menu-language"
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                      >
                        <Languages className="h-5 w-5 text-gray-500" />
                        <span>Language</span>
                        <span className="ml-auto text-emerald-600 font-medium">{currentLang.short}</span>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showLanguageMenu ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {showLanguageMenu && (
                        <div className="pl-8 pr-2 pb-2">
                          {LANGUAGES.map(lang => (
                            <button
                              key={lang.code}
                              onClick={() => {
                                setLanguage(lang.code);
                                setShowLanguageMenu(false);
                              }}
                              className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${
                                language === lang.code 
                                  ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600' 
                                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                              }`}
                            >
                              <span>{lang.label}</span>
                              {language === lang.code && <Check className="h-4 w-4" />}
                            </button>
                          ))}
                          {hasUserPreference && (
                            <button
                              onClick={() => { 
                                resetToSiteDefault(); 
                                setShowLanguageMenu(false); 
                              }}
                              className="w-full mt-1 px-3 py-2 text-xs text-gray-500 hover:text-emerald-600 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg"
                            >
                              Reset to site default
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Create Post */}
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        onOpenCreator?.();
                      }}
                      data-testid="menu-create-post"
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                    >
                      <PenSquare className="h-5 w-5 text-gray-500" />
                      <span>Create Post</span>
                    </button>

                    {/* Settings */}
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        onOpenSettings?.();
                      }}
                      data-testid="menu-settings"
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                    >
                      <Settings className="h-5 w-5 text-gray-500" />
                      <span>Settings</span>
                    </button>
                  </div>

                  {/* Admin & Sign Out Section */}
                  {isAuthenticated && (
                    <div className="border-t border-gray-200 dark:border-gray-800 p-2">
                      {isAdmin && (
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            onOpenAdmin?.();
                          }}
                          data-testid="menu-admin"
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                        >
                          <Shield className="h-5 w-5 text-emerald-500" />
                          <span>Admin Panel</span>
                          <PendingBadge />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          logout();
                        }}
                        data-testid="menu-sign-out"
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                      >
                        <LogOut className="h-5 w-5" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Category Navigation */}
        <nav className="relative border-t border-gray-100/80 dark:border-gray-800/80">
          <div 
            className="flex items-center px-2 sm:px-4 md:px-8 lg:px-12 h-10 sm:h-12 justify-between md:justify-between"
          >
            {categories.map((cat, index) => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex-1 md:flex-initial h-full px-1 sm:px-2 md:px-2 lg:px-4 text-[11px] sm:text-sm whitespace-nowrap transition-all flex items-center justify-center cursor-grab active:cursor-grabbing relative ${
                    isActive
                      ? 'text-emerald-700 dark:text-emerald-400 font-bold'
                      : 'text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-300 font-medium'
                  } ${draggedIndex === index ? 'opacity-50' : ''}`}
                >
                  {cat.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-1 right-1 h-[2.5px] bg-emerald-500 dark:bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.7)]" />
                  )}
                </button>
              );
            })}
          </div>
        </nav>

      </div>
      
      {/* Notification Panel */}
      <NotificationPanel 
        isOpen={showNotifications} 
        onClose={() => setShowNotifications(false)}
        unreadCount={unreadCount}
        setUnreadCount={setUnreadCount}
        onUnreadCountChange={() => {
          if (isAuthenticated) {
            const token = localStorage.getItem('yegoNewsToken');
            axios.get(`${BACKEND_URL}/api/notifications/unread-count`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(res => setUnreadCount(res.data.count)).catch(() => {});
          }
        }} 
      />
    </header>
  );
};

export default RwandaNewsHeader;
